'use client';

import type { ConfiguratorFieldFnOptions } from '@eliadmin/core/src/components/configurator/configurator-field-section';
import {
  ConfiguratorFieldSection,
  type ConfiguratorSection,
} from '@eliadmin/core/src/components/configurator/configurator-field-section';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { GenericLoader } from '@eliadmin/core/src/components/ui/generic-loader';
import { Label } from '@eliadmin/core/src/components/ui/label';
import type { TranslateFn } from '@eliadmin/core/src/hooks/use-translations';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import type {
  FieldDataItem,
  ProductConfIdColumn,
} from '@eliadmin/core/src/stores/configurator-store';
import {
  useConfiguratorStore,
  useProformQuery,
  getValueFromConfigId,
} from '@eliadmin/core/src/stores/configurator-store';
import {
  extractDataFromField,
  extractProformArray,
  isEmpty,
} from '@eliadmin/core/src/utils/proform-utils';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import React, { Suspense } from 'react';

const showOrnamentFunc = (index: number) => (options: ConfiguratorFieldFnOptions) => {
  for (let i = 1; i <= index; i++) {
    const value = options.productConfId.root.smako_conf.column[`user_m${i}` as ProductConfIdColumn];
    if (!value || value === '0') return false;
  }

  return true;
};

const orderGrp = (a: FieldDataItem, b: FieldDataItem) => {
  let result = 0;
  if (a.poradi_zobrazeni && b.poradi_zobrazeni) {
    const numA = parseInt(a.poradi_zobrazeni, 10);
    const numB = parseInt(b.poradi_zobrazeni, 10);
    result = numA - numB;
  }

  if (result !== 0) return result;

  if (a.kod && b.kod) {
    return a.kod.localeCompare(b.kod);
  }

  return 0;
};

export const FIELD_SECTIONS = (t: TranslateFn): Record<string, ConfiguratorSection> => {
  return {
    'povrchove-upravy': {
      title: t('text1455'),
      getOrder: () => 1,
      isVisible: ({ productConf }) => {
        return !!productConf.root.smako_conf_grp_folie;
      },
      fields: [
        {
          id: 'user_grp_folie',
          type: 'select',
          orderBy: orderGrp,
        },
        {
          id: 'user_kod_folie',
          type: 'select',
          filterFn: (fieldData, { productConfId }) => {
            return fieldData.filter(item => {
              const grpValue = productConfId.root.smako_conf.column.user_grp_folie;
              if (!isEmpty(grpValue)) {
                return grpValue === item.kod_grp;
              }

              return true;
            });
          },
        },
        {
          id: 'user_smer_let',
          type: 'radio',
          variant: 'orientation',
          isVisible: opts => {
            const selectedFolie = extractDataFromField('user_kod_folie', opts.productConf)?.find(
              i => i.kod === opts.productConfId.root.smako_conf.column.user_kod_folie,
            );

            if (selectedFolie && selectedFolie.smer_let === '1') {
              return true;
            }

            return false;
          },
        },
        {
          id: 'user_patina',
          type: 'select',
          renderValueLabel: value => value?.popis ?? '',
          isVisible: opts => {
            const data = (extractDataFromField('user_patina', opts.productConf) ?? []) as any[];
            // eslint-disable-next-line no-console
            console.log('[user_patina] isVisible check', {
              length: data.length,
              hasNonZero: data.some(p => p.kod !== '0'),
              sample: data.slice(0, 5),
            });
            // show only if we have patina with kod !== 0
            return data.some(p => p.kod !== '0');
          },
        },
        {
          id: 'user_folie_spec',
          type: 'select',
          isVisible: opts => {
            const selectedFolie = extractDataFromField('user_kod_folie', opts.productConf)?.find(
              i => i.kod === opts.productConfId.root.smako_conf.column.user_kod_folie,
            );

            if (selectedFolie && selectedFolie.folie_spec !== '0') {
              return true;
            }

            return false;
          },
          filterFn: (fieldData, { productConfId, productConf }) => {
            const selectedFolie = extractDataFromField('user_kod_folie', productConf)?.find(
              i => i.kod === productConfId.root.smako_conf.column.user_kod_folie,
            );

            if (!selectedFolie) return fieldData;

            // const value = item.folie_spec;
            return fieldData.filter(item => {
              return selectedFolie.folie_spec === item.folie_spec;
            });
          },
        },
      ],
    },
    'povrchove-upravy-zad': {
      getOrder: () => 2,
      title: t('text1456'),
      isVisible: ({ productConf }) => {
        return !!productConf.root.smako_conf_grp_zada;
      },
      fields: [
        { id: 'user_grp_zada', type: 'select', orderBy: orderGrp },
        {
          id: 'user_kod_zada',
          type: 'select',
          filterFn: (fieldData, { productConfId }) => {
            return fieldData.filter(item => {
              const grpValue = productConfId.root.smako_conf.column.user_grp_zada;
              if (!isEmpty(grpValue)) {
                return grpValue === item.kod_grp;
              }
              return true;
            });
          },
        },
        // TODO: toto nechodi z be bude potrebne robit request na kazdy kod samostatne asi
        {
          id: 'user_zada_spec',
          type: 'select',
          isVisible: opts => {
            const selectedZada = extractDataFromField('user_kod_zada', opts.productConf)?.find(
              i => i.kod === opts.productConfId.root.smako_conf.column.user_kod_zada,
            );

            if (selectedZada && selectedZada.zada_spec !== '0') {
              return true;
            }

            return false;
          },
          filterFn: (fieldData, { productConfId, productConf }) => {
            const selectedZada = extractDataFromField('user_kod_zada', productConf)?.find(
              i => i.kod === productConfId.root.smako_conf.column.user_kod_zada,
            );
            if (!selectedZada) return fieldData;
            return fieldData.filter(item => {
              return selectedZada.zada_spec === item.zada_spec;
            });
          },
        },
      ],
    },
    'provedeni-dvirek': {
      getOrder: () => 3,
      title: t('text1457'),
      isVisible: opts => {
        return (
          !!opts.productConf.root.smako_conf_grp_prov || !!opts.productConf.root.smako_conf_kod_prov
        );
      },
      fields: [
        {
          id: 'user_grp_prov',
          type: 'select',
          orderBy: orderGrp,
          isVisible: opts => {
            const arr = extractProformArray(opts.productConf.root.smako_conf_grp_prov.grp_prov);
            return arr.length > 1 || arr[0]?.kod !== '';
          },
        },
        {
          id: 'user_kod_prov',
          type: 'select',

          filterFn: (fieldData, { productConfId }) => {
            return fieldData.filter(item => {
              const grpValue = productConfId.root.smako_conf.column.user_grp_prov;
              if (!isEmpty(grpValue)) {
                return grpValue === item.kod_grp;
              }
              return true;
            });
          },
        },
        {
          id: 'user_sila',
          type: 'select',
          isVisible: opts => {
            const arr = extractProformArray(opts.productConf.root.smako_conf_sila.sila);
            return arr.length > 1 || arr[0]?.kod !== '';
          },
        },
        // sklo
        {
          id: 'user_grp_skla',
          orderBy: orderGrp,
          type: 'select',
          isVisible: opts => {
            const selectedProv = extractDataFromField('user_kod_prov', opts.productConf)?.find(
              i => i.kod === opts.productConfId.root.smako_conf.column.user_kod_prov,
            );

            return !!selectedProv && selectedProv.prov_sklo !== '0';
          },
        },
        {
          id: 'user_kod_skla',
          type: 'select',
          isVisible: opts => {
            const selectedProv = extractDataFromField('user_kod_prov', opts.productConf)?.find(
              i => i.kod === opts.productConfId.root.smako_conf.column.user_kod_prov,
            );

            return !!selectedProv && selectedProv.prov_sklo !== '0';
          },
          filterFn: (fieldData, { productConfId }) => {
            return fieldData.filter(item => {
              const grpValue = productConfId.root.smako_conf.column.user_grp_skla;
              if (!isEmpty(grpValue)) {
                return grpValue === item.kod_grp;
              }
              return true;
            });
          },
        },
        {
          id: 'user_sklo_spec',
          type: 'select',
          isVisible: opts => {
            const selectedSklo = extractDataFromField('user_kod_skla', opts.productConf)?.find(
              i => i.kod === opts.productConfId.root.smako_conf.column.user_kod_skla,
            );
            const selectedProv = extractDataFromField('user_kod_prov', opts.productConf)?.find(
              i => i.kod === opts.productConfId.root.smako_conf.column.user_kod_prov,
            );

            return (
              !!selectedSklo &&
              selectedSklo.sklo_spec !== '0' &&
              !!selectedProv &&
              selectedProv.prov_sklo !== '0'
            );
          },
          filterFn: (fieldData, { productConfId, productConf }) => {
            const selectedSklo = extractDataFromField('user_kod_skla', productConf)?.find(
              i => i.kod === productConfId.root.smako_conf.column.user_kod_skla,
            );
            if (!selectedSklo) return fieldData;
            return fieldData.filter(item => {
              return selectedSklo.sklo_spec === item.sklo_spec;
            });
          },
        },
        // TODO: poprosit specifikaciu
        {
          id: 'user_smer_skla',
          type: 'radio',
          variant: 'orientation',
          isVisible: opts => {
            const selectedSklo = extractDataFromField('user_kod_prov', opts.productConf)?.find(
              i => i.kod === opts.productConfId.root.smako_conf.column.user_kod_prov,
            );

            return !!selectedSklo && !['0', '2'].includes(selectedSklo.prov_sklo);
          },
        },
        // TODO: poprosit specifikaciu
        {
          id: 'user_ornament_skla',
          type: 'radio',
          variant: 'placing',
          isVisible: opts => {
            const selectedSklo = extractDataFromField('user_kod_prov', opts.productConf)?.find(
              i => i.kod === opts.productConfId.root.smako_conf.column.user_kod_prov,
            );

            return !!selectedSklo && !['0', '2'].includes(selectedSklo.prov_sklo);
          },
        },
      ],
    },
    frezovani: {
      getOrder: () => 4,
      title: 'Frézování',
      isVisible: ({ productConf }) => {
        return !!productConf.root.smako_conf_kod_frezovani;
      },
      fields: [
        {
          id: 'user_kod_frezovani',
          type: 'select',
        },
      ],
    },
    hrany: {
      getOrder: () => 4,
      title: t('text1458'),
      isVisible: opts => {
        return !!opts.productConf.root.smako_conf_hrana;
      },
      fields: [
        {
          id: 'user_hrana_h',
          type: 'select',
          getFieldData: opts => {
            const result = opts.productConf.root.smako_conf_hrana.hrana;
            if (!result) return [];
            return Array.isArray(result) ? result : [result];
          },
        },
        {
          id: 'user_hrana_p',
          type: 'select',
          getFieldData: opts => {
            const result = opts.productConf.root.smako_conf_hrana.hrana;
            if (!result) return [];
            return Array.isArray(result) ? result : [result];
          },
        },
        {
          id: 'user_hrana_d',
          type: 'select',
          getFieldData: opts => {
            const result = opts.productConf.root.smako_conf_hrana.hrana;
            if (!result) return [];
            return Array.isArray(result) ? result : [result];
          },
        },
        {
          id: 'user_hrana_l',
          type: 'select',
          getFieldData: opts => {
            const result = opts.productConf.root.smako_conf_hrana.hrana;
            if (!result) return [];
            return Array.isArray(result) ? result : [result];
          },
        },
      ],
    },
    uchytky: {
      getOrder: () => 4,
      title: t('text1459'),
      isVisible: opts => {
        return (
          !!FIELD_SECTIONS(t).uchytky.fields.some(
            // @ts-expect-error we just null-checking
            ({ id }) => !!opts.productConf.root[id.replace('user_', 'smako_conf_')],
          ) && !opts.productConf.root.smako_conf_ornament
        );
      },
      fields: [
        { id: 'user_grp_pantu', type: 'select', orderBy: orderGrp },
        {
          id: 'user_kod_pantu',
          type: 'select',
          filterFn: (fieldData, { productConfId }) => {
            return fieldData.filter(item => {
              const grpValue = productConfId.root.smako_conf.column.user_grp_pantu;
              if (!isEmpty(grpValue)) {
                return grpValue === item.kod_grp;
              }
              return true;
            });
          },
        },
        {
          id: 'user_pant_povrch',
          type: 'select',
          filterFn: (fieldData, { productConfId }) => {
            const selectedPant = productConfId.root.smako_conf.column.user_kod_pantu;
            if (!selectedPant) return fieldData;
            return fieldData.filter(item => {
              return selectedPant === item.kod_pantu;
            });
          },
        },
        {
          id: 'user_pant_spec',
          type: 'select',
          isVisible: opts => {
            const selectedPantPovrch = extractDataFromField(
              'user_pant_povrch',
              opts.productConf,
            )?.find(i => i.kod === opts.productConfId.root.smako_conf.column.user_pant_povrch);

            if (selectedPantPovrch && selectedPantPovrch.pant_spec !== '0') {
              return true;
            }

            return false;
          },
          filterFn: (fieldData, { productConfId, productConf }) => {
            const selectedPantPovrch = extractDataFromField('user_pant_povrch', productConf)?.find(
              i => i.kod === productConfId.root.smako_conf.column.user_pant_povrch,
            );
            if (!selectedPantPovrch) return fieldData;
            return fieldData.filter(item => {
              return selectedPantPovrch.pant_spec === item.pant_spec;
            });
          },
        },
        {
          id: 'user_pant_umi',
          type: 'select',
          isVisible: opts => {
            const data = extractDataFromField('user_kod_pantu', opts.productConf);

            const selectedGrpPantu = data?.find(
              i => i.kod === opts.productConfId.root.smako_conf.column.user_kod_pantu,
            );

            return !!selectedGrpPantu;
          },
        },
      ],
    },
    panty: {
      getOrder: opts => {
        if (!opts.productConf.root.smako_conf_ornament) {
          return 5;
        }

        return 6;
      },
      title: t('text1460'),
      isVisible: opts => {
        return !!opts.productConf.root.smako_conf_vrtani;
      },
      fields: [
        { id: 'user_vrtani', type: 'select' },
        { id: 'user_kovani', type: 'select' },
        { id: 'user_vyklop', type: 'select' },
      ],
    },
    ornamenty: {
      getOrder: () => 7,
      title: t('text1461'),
      fields: [
        // the user_m1_o id doesnt make sense it is just a hacky way to render the reset component
        { type: 'ornament_reset', id: 'user_m1_o' },
        {
          id: 'user_m1',
          type: 'ornament',
          className: 'col-span-3',
        },
        {
          id: 'user_m2',
          type: 'ornament',
          className: 'col-span-3',
          isVisible: showOrnamentFunc(1),
        },
        {
          id: 'user_m3',
          type: 'ornament',
          className: 'col-span-3',
          isVisible: showOrnamentFunc(2),
        },
        {
          id: 'user_m4',
          type: 'ornament',
          className: 'col-span-3',
          isVisible: showOrnamentFunc(3),
        },
        {
          id: 'user_m5',
          type: 'ornament',
          className: 'col-span-3',
          isVisible: showOrnamentFunc(4),
        },
        {
          id: 'user_m6',
          type: 'ornament',
          className: 'col-span-3',
          isVisible: showOrnamentFunc(5),
        },
        {
          id: 'user_m7',
          type: 'ornament',
          className: 'col-span-3',
          isVisible: showOrnamentFunc(6),
        },
        {
          id: 'user_m8',
          type: 'ornament',
          className: 'col-span-3',
          isVisible: showOrnamentFunc(7),
        },
        {
          id: 'user_m9',
          type: 'ornament',
          className: 'col-span-3',
          isVisible: showOrnamentFunc(8),
        },
      ],
      isVisible: opts => {
        return !!opts.productConf.root.smako_conf_ornament;
      },
    },
    rozmery: {
      getOrder: opts => {
        if (!opts.productConf.root.smako_conf_ornament) {
          return 6;
        }

        return 5;
      },
      title: t('text1462'),
      fields: [
        { id: 'user_vyska', type: 'range', variant: 'vyska' },
        { id: 'user_sirka', type: 'range', variant: 'sirka' },
        { id: 'mnozstvi', type: 'range', variant: 'mnozstvi' },
        { id: 'user_expres', type: 'switch' },
      ],
    },
    'doplnujuci-udaje': {
      getOrder: () => 8,
      title: t('text1463'),
      isVisible: opts => {
        return !!opts.productCols.root.smako_conf_cols.column.find(
          i => i.column === 'user_poznamka',
        );
      },
      fields: [{ id: 'user_poznamka', type: 'textarea', className: 'col-span-2' }],
    },
  };
};

export function ConfiguratorAccordion() {
  const productConf = useConfiguratorStore(s => s.productConf);
  const productDetail = useConfiguratorStore(s => s.productDetail);
  const productCols = useConfiguratorStore(s => s.productCols);
  const user_konf_id = useConfiguratorStore(s => s.user_konf_id);
  const { data: productConfId } = useProformQuery('GET_PRODUCT_CONF_ID', { user_konf_id });

  const { t } = useTranslations();

  const visibleFieldSections = Object.keys(FIELD_SECTIONS(t)).reduce<Record<string, boolean>>(
    (acc, key) => {
      const fieldSection = FIELD_SECTIONS(t)[key];
      if (fieldSection.isVisible) {
        acc[key] = fieldSection.isVisible({ productConf, productCols });
      } else {
        acc[key] = true;
      }

      return acc;
    },
    {},
  );

  const fieldSectionKeys = Object.keys(visibleFieldSections)
    .filter(key => visibleFieldSections[key])
    .filter(key => {
      const fieldSection = FIELD_SECTIONS(t)[key];
      // Keep section only if at least one field is visible
      return fieldSection.fields.some(f => {
        const fn = typeof (f as any).isVisible === 'function' ? (f as any).isVisible : undefined;
        if (!productConfId) return true;
        // Build fieldConfig similarly to ConfiguratorField
        let fieldCol = productCols.root.smako_conf_cols?.column.find(
          col => col?.column === (f as any).id,
        );
        if ((f as any).type === 'range' && (f as any).variant === 'mnozstvi') {
          fieldCol = {
            column: 'mnozstvi',
            help: t('text1464'),
            label: t('text1465'),
            must: '1',
          } as any;
        }
        if ((f as any).type === 'ornament') {
          const index = Number(String((f as any).id).replace('user_m', ''));
          fieldCol = {
            column: (f as any).id,
            help: '',
            label: `${index}.`,
            must: '0',
          } as any;
        }
        if (!fieldCol) return false;
        const defaultValues = productDetail.root.smako_nom_default?.default as any;
        const value = getValueFromConfigId(productConfId as any, fieldCol.column, defaultValues);
        const options = {
          fieldProps: { field: f, fieldConfig: fieldCol } as any,
          productConfId,
          productConf: productConf as any,
          value,
        } as any;
        return fn ? !!fn(options) : true;
      });
    })
    .sort((a, b) => {
      const aOrder = FIELD_SECTIONS(t)[a].getOrder({ productConf, productDetail });
      const bOrder = FIELD_SECTIONS(t)[b].getOrder({ productConf, productDetail });
      return aOrder - bOrder;
    });

  const [openSections, setOpenSections] = React.useState<string[]>([]);
  const areAllDisplayed = openSections.length === fieldSectionKeys.length;
  const errors = useConfiguratorStore(s => s.errorFields);

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <p className="text-lg font-medium text-[#333333] md:text-2xl">
          {t('text1454')}:{' '}
          <span className="text-accent">{productDetail.root.nomenklatura_detail?.nazev}</span>
        </p>

        <Label className="flex items-center justify-end gap-2 whitespace-nowrap text-sm text-accent">
          <Checkbox
            className="border-accent data-[state=checked]:bg-accent data-[state=checked]:text-secondary"
            checked={areAllDisplayed}
            onCheckedChange={checked => {
              if (checked) {
                setOpenSections(fieldSectionKeys);
              } else {
                setOpenSections([]);
              }
            }}
          />
          {t('text1453')}
        </Label>
      </div>

      <Accordion
        type="multiple"
        className={cn('ea-accordion flex w-full flex-col gap-4 rounded-xl shadow-none')}
        value={openSections}
        onValueChange={setOpenSections}
      >
        {fieldSectionKeys.map((key, index) => {
          const fieldSection = FIELD_SECTIONS(t)[key];
          const hasError = fieldSection.fields.some(f => errors[f.id]);

          return (
            <AccordionItem
              key={key}
              value={key}
              className={cn(
                'rounded-xl border border-muted data-[state=open]:bg-secondary',
                hasError && 'border-destructive',
              )}
            >
              <AccordionTrigger className="flex w-full flex-row-reverse items-center gap-4 p-4">
                <div className="flex flex-1 justify-between">
                  <span className="text-foreground">{fieldSection.title}</span>
                  <span className="text-muted-foreground">
                    {index + 1} / {fieldSectionKeys.length}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent
                className={cn('p-4 @container')}
                // Ak sa odstrani force mount treba vymazat zakomentovanu cast v proform-validation.tsx, nebudu totiz automaticky fungovat validacie
                forceMount
                hidden={!openSections.includes(key)}
              >
                <Suspense fallback={<GenericLoader className="max-h-[250px]" />}>
                  <ConfiguratorFieldSection fieldSection={fieldSection} />
                </Suspense>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}
